import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

export const VIEW_STATE_FEATURE_KEY = 'viewState';

export interface ViewState {
  query: string;
  selectedThemeId: number | null;
}

interface ViewStateSlice {
  byKey: {
    [viewStateKey: string]: ViewState;
  };
}

const initialViewState: ViewState = {
  query: '',
  selectedThemeId: null,
};

const initialState: ViewStateSlice = {
  byKey: {},
};

export const viewStateSlice = createSlice({
  name: VIEW_STATE_FEATURE_KEY,
  initialState,
  reducers: {
    setViewState: (
      state,
      action: PayloadAction<{
        viewStateKey: string;
        viewState: Partial<ViewState>;
      }>
    ) => {
      const { viewStateKey, viewState } = action.payload;
      if (!state.byKey[viewStateKey]) {
        state.byKey[viewStateKey] = { ...initialViewState };
      }
      state.byKey[viewStateKey] = {
        ...state.byKey[viewStateKey],
        ...viewState,
      };
    },
    setSelectedThemeId: (
      state,
      action: PayloadAction<{ viewStateKey: string; themeId: number | null }>
    ) => {
      const { viewStateKey, themeId } = action.payload;
      if (!state.byKey[viewStateKey]) {
        state.byKey[viewStateKey] = { ...initialViewState };
      }
      state.byKey[viewStateKey].selectedThemeId = themeId;
    },
    setQuery: (
      state,
      action: PayloadAction<{ viewStateKey: string; query: string }>
    ) => {
      const { viewStateKey, query } = action.payload;
      if (!state.byKey[viewStateKey]) {
        state.byKey[viewStateKey] = { ...initialViewState };
      }
      state.byKey[viewStateKey].query = query;
    },
    clearViewState: (state, action: PayloadAction<{ viewStateKey: string }>) => {
      const { viewStateKey } = action.payload;
      state.byKey[viewStateKey] = { ...initialViewState };
    },
    clearAllViewState: (state) => {
      state.byKey = {};
    },
  },
});

export const viewStateReducer = viewStateSlice.reducer;

export const {
  setViewState,
  setSelectedThemeId,
  setQuery,
  clearViewState,
  clearAllViewState,
} = viewStateSlice.actions;

// Selectors
const selectViewStateRoot = (state: RootState) => state[VIEW_STATE_FEATURE_KEY];

export const viewStateSelectors = {
  getViewState: (viewStateKey: string) => (state: RootState): ViewState => {
    const viewState = selectViewStateRoot(state)?.byKey[viewStateKey];
    return viewState || initialViewState;
  },
  getSelectedThemeId: (viewStateKey: string) => (state: RootState): number | null => {
    return selectViewStateRoot(state)?.byKey[viewStateKey]?.selectedThemeId ?? null;
  },
  getQuery: (viewStateKey: string) => (state: RootState): string => {
    return selectViewStateRoot(state)?.byKey[viewStateKey]?.query ?? '';
  },
};
